"use client";

import type { ReportData } from "./reportData";

interface AvatarEssenceSectionProps {
  report: ReportData;
}

export default function AvatarEssenceSection({ report }: AvatarEssenceSectionProps) {
  const { essence } = report;

  return (
    <section className="fade-in-up">
      {/* Section Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="section-number">1</div>
        <div>
          <h2
            className="text-xl md:text-2xl font-display text-white/95"
            style={{ fontWeight: 700 }}
          >
            Your Avatar Essence
          </h2>
          <p className="text-sm text-white/45 mt-0.5" style={{ fontWeight: 400 }}>
            Archetype Overview · Core Traits · Strengths · Growth Challenges
          </p>
        </div>
      </div>

      {/* Archetype Overview */}
      <div className="nautilus-card p-6 md:p-8 mb-5">
        <div className="flex items-center gap-2 mb-4">
          <div
            className="w-1 h-5 rounded-full"
            style={{ background: "linear-gradient(180deg, #dbb84d, rgba(219,184,77,0.3))" }}
          />
          <h3
            className="text-base font-display text-white/90"
            style={{ fontWeight: 600 }}
          >
            Archetype Overview
          </h3>
        </div>
        <p className="text-sm md:text-base text-white/70 leading-relaxed" style={{ fontWeight: 400 }}>
          {essence.archetypeOverview}
        </p>
      </div>

      {/* Core Traits */}
      <div className="nautilus-card p-6 md:p-8 mb-5">
        <div className="flex items-center gap-2 mb-5">
          <div
            className="w-1 h-5 rounded-full"
            style={{ background: "linear-gradient(180deg, #dbb84d, rgba(219,184,77,0.3))" }}
          />
          <h3
            className="text-base font-display text-white/90"
            style={{ fontWeight: 600 }}
          >
            Core Traits
          </h3>
        </div>
        <div className="flex flex-wrap gap-2.5">
          {essence.coreTraits.map((trait, i) => (
            <span key={i} className="trait-pill">
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: "#dbb84d" }}
              />
              {trait}
            </span>
          ))}
        </div>
      </div>

      {/* Strengths + Growth Challenges — Two Column */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Strengths */}
        <div className="nautilus-card p-6 md:p-8">
          <div className="flex items-center gap-2 mb-5">
            <div
              className="w-1 h-5 rounded-full"
              style={{ background: "linear-gradient(180deg, #dbb84d, rgba(219,184,77,0.3))" }}
            />
            <h3
              className="text-base font-display text-white/90"
              style={{ fontWeight: 600 }}
            >
              Natural Strengths
            </h3>
          </div>
          <div>
            {essence.strengths.map((strength, i) => (
              <div key={i} className="strength-item">
                <div className="strength-dot" />
                <p className="text-sm text-white/75 leading-relaxed" style={{ fontWeight: 400 }}>
                  {strength}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Growth Challenges */}
        <div className="nautilus-card p-6 md:p-8">
          <div className="flex items-center gap-2 mb-5">
            <div
              className="w-1 h-5 rounded-full"
              style={{ background: "linear-gradient(180deg, #7c3e61, rgba(124,62,97,0.3))" }}
            />
            <h3
              className="text-base font-display text-white/90"
              style={{ fontWeight: 600 }}
            >
              Growth Challenges
            </h3>
          </div>
          <div>
            {essence.growthChallenges.map((challenge, i) => (
              <div key={i} className="strength-item">
                <div className="growth-dot" />
                <p className="text-sm text-white/75 leading-relaxed" style={{ fontWeight: 400 }}>
                  {challenge}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}